import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import feedbackService from '../../services/feedbackService';
import './DataCorrectionModal.scss';

const DataCorrectionModal = ({ 
  isOpen, 
  onClose, 
  attempts, 
  currentItem, 
  categories, 
  gameName,
  itemName 
}) => {
  const { user, isAuthenticated } = useAuth();
  const { addToast } = useToast();
  
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedCategories, setSelectedCategories] = useState({});
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const allItems = [...attempts.map(attempt => attempt[itemName]), currentItem].filter(Boolean);
  const uniqueItems = Array.from(new Map(allItems.map(item => [item.name, item])).values());

  const categoryOptions = [
    ...categories.map(cat => ({ key: cat.key, label: cat.label })),
    { key: 'hint', label: 'Hint' }
  ];

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleCategoryChange = (categoryKey) => {
    setSelectedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedItem) {
      addToast({
        message: 'Please select an item to report data for.',
        type: 'error',
        autoClose: true
      });
      return;
    }

    const selectedCategoryKeys = Object.keys(selectedCategories).filter(key => selectedCategories[key]);
    if (selectedCategoryKeys.length === 0) {
      addToast({
        message: 'Please select at least one category with incorrect data.',
        type: 'error',
        autoClose: true
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await feedbackService.submitDataCorrection({
        gameName,
        itemName: selectedItem,
        incorrectCategories: selectedCategoryKeys,
        additionalNotes
      });

      addToast({
        message: 'Thank you! Your data correction report has been submitted.',
        type: 'success',
        autoClose: true
      });

      onClose();
      setSelectedItem('');
      setSelectedCategories({});
      setAdditionalNotes('');
    } catch (error) {
      console.error('Error submitting data correction:', error);
      addToast({
        message: 'Failed to submit data correction. Please try again.',
        type: 'error',
        autoClose: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="data-correction-modal__overlay" onClick={handleOverlayClick}>
      <div className="data-correction-modal__container">
        <div className="data-correction-modal__header">
          <h2 className="data-correction-modal__title">Report Incorrect Data</h2>
          <button 
            onClick={onClose}
            className="data-correction-modal__close-button"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="data-correction-modal__form">
          <div className="data-correction-modal__field">
            <label className="data-correction-modal__label">
              Which item has incorrect data?
            </label>
            <select
              value={selectedItem}
              onChange={(e) => setSelectedItem(e.target.value)}
              className="data-correction-modal__select"
              required
            >
              <option value="">Select an item...</option>
              {uniqueItems.map(item => (
                <option key={item.name} value={item.name}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          <div className="data-correction-modal__field">
            <label className="data-correction-modal__label">
              Please indicate which category had incorrect info:
            </label>
            <div className="data-correction-modal__checkboxes">
              {categoryOptions.map(category => (
                <label key={category.key} className="data-correction-modal__checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedCategories[category.key] || false}
                    onChange={() => handleCategoryChange(category.key)}
                    className="data-correction-modal__checkbox"
                  />
                  {category.label}
                </label>
              ))}
            </div>
          </div>

          <div className="data-correction-modal__field">
            <label className="data-correction-modal__label">
              Additional information (optional)
            </label>
            <textarea
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              placeholder="Optionally include any additional info or feedback."
              className="data-correction-modal__textarea"
              rows="4"
            />
          </div>

          <div className="data-correction-modal__footer">
            <button
              type="button"
              onClick={onClose}
              className="data-correction-modal__cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="data-correction-modal__submit-btn"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DataCorrectionModal;